import { useMemo, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  OverlayTrigger,
  Pagination,
  Row,
  Spinner,
  Table,
  Tooltip
} from 'react-bootstrap'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { api } from '../../services/api'

interface ApiKey {
  id: string
  label: string
  systemName: string
  isActive: boolean
  lastUsedAt: string | null
  expiresAt: string | null
  createdAt: string
  updatedAt: string
  metadata: Record<string, unknown> | null
}

interface ApiKeyListResponse {
  data: ApiKey[]
  pagination?: {
    page: number
    pageSize: number
    total: number
    totalPages: number
  }
}

interface CreateApiKeyResponse {
  success: boolean
  data: ApiKey & { key?: string }
  message?: string
}

type FormState = {
  label: string
  systemName: string
  expiresAt: string
  metadata: string
}

const DEFAULT_FORM_STATE: FormState = {
  label: '',
  systemName: '',
  expiresAt: '',
  metadata: ''
}

const PAGE_SIZE = 10

export const APIKeysPage = () => {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [showFormModal, setShowFormModal] = useState(false)
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null)
  const [formState, setFormState] = useState<FormState>(DEFAULT_FORM_STATE)
  const [generatedKey, setGeneratedKey] = useState<{ label: string; key: string } | null>(null)

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['api-keys', page, PAGE_SIZE],
    queryFn: async (): Promise<ApiKeyListResponse> => {
      const response = await api.get('/admin/api-keys', {
        params: { page, pageSize: PAGE_SIZE }
      })
      return response.data
    },
    placeholderData: keepPreviousData
  })

  const resetForm = () => {
    setFormState(DEFAULT_FORM_STATE)
    setEditingKey(null)
  }

  const openCreateModal = () => {
    resetForm()
    setShowFormModal(true)
  }

  const openEditModal = (apiKey: ApiKey) => {
    setEditingKey(apiKey)
    setFormState({
      label: apiKey.label,
      systemName: apiKey.systemName,
      expiresAt: apiKey.expiresAt ? apiKey.expiresAt.slice(0, 16) : '',
      metadata: apiKey.metadata ? JSON.stringify(apiKey.metadata, null, 2) : ''
    })
    setShowFormModal(true)
  }

  const closeFormModal = () => {
    setShowFormModal(false)
  }

  const closeGeneratedKeyModal = () => {
    setGeneratedKey(null)
  }

  const listInvalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['api-keys'] })
  }

  const createMutation = useMutation<CreateApiKeyResponse, any, any>({
    mutationFn: async (payload) => {
      const response = await api.post('/admin/api-keys', payload)
      return response.data
    },
    onSuccess: (result) => {
      listInvalidate()
      setShowFormModal(false)
      resetForm()
      if (result.data?.key) {
        setGeneratedKey({ label: result.data.label, key: result.data.key })
      }
      toast.success(result.message || 'API key created successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create API key')
    }
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: Record<string, unknown> }) => {
      const response = await api.patch(`/admin/api-keys/${id}`, payload)
      return response.data
    },
    onSuccess: () => {
      listInvalidate()
      setShowFormModal(false)
      resetForm()
      toast.success('API key updated successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update API key')
    }
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/api-keys/${id}`)
    },
    onSuccess: () => {
      listInvalidate()
      toast.success('API key deleted successfully')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete API key')
    }
  })

  const toggleStatusMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const response = await api.patch(`/admin/api-keys/${id}`, { isActive })
      return response.data
    },
    onSuccess: (_, variables) => {
      listInvalidate()
      toast.success(variables.isActive ? 'API key activated' : 'API key suspended')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update key status')
    }
  })

  const apiKeys: ApiKey[] = data?.data ?? []
  const pagination = data?.pagination
  const totalPages = pagination?.totalPages ?? 1

  const formatDateTime = (value?: string | null) => {
    if (!value) return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '—'
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    let metadataPayload: Record<string, unknown> | null = null
    if (formState.metadata.trim().length > 0) {
      try {
        metadataPayload = JSON.parse(formState.metadata)
      } catch (error) {
        toast.error('Metadata must be valid JSON')
        return
      }
    }

    const payload: Record<string, unknown> = {
      label: formState.label,
      systemName: formState.systemName,
      metadata: metadataPayload ?? undefined
    }

    if (formState.expiresAt) {
      const date = new Date(formState.expiresAt)
      if (Number.isNaN(date.getTime())) {
        toast.error('Expiration date is invalid')
        return
      }
      payload.expiresAt = date.toISOString()
    } else {
      payload.expiresAt = null
    }

    if (editingKey) {
      updateMutation.mutate({ id: editingKey.id, payload })
    } else {
      createMutation.mutate(payload)
    }
  }

  const paginationItems = useMemo(() => {
    if (totalPages <= 1) {
      return null
    }

    return (
      <Pagination className="mb-0">
        <Pagination.Prev
          onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
          disabled={page === 1 || isFetching}
        />
        {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
          <Pagination.Item
            key={pageNumber}
            active={pageNumber === page}
            onClick={() => setPage(pageNumber)}
            disabled={isFetching}
          >
            {pageNumber}
          </Pagination.Item>
        ))}
        <Pagination.Next
          onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={page === totalPages || isFetching}
        />
      </Pagination>
    )
  }, [page, totalPages, isFetching])

  return (
    <div>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1">
                <i className="fas fa-key me-2 text-primary"></i>
                API Keys
              </h2>
              <p className="text-muted mb-0">
                Manage system-to-system access for external integrations
              </p>
            </div>
            <Button variant="primary" onClick={openCreateModal} className="d-flex align-items-center">
              <i className="fas fa-plus me-2"></i>
              Create API Key
            </Button>
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-0">
                  <i className="fas fa-list me-2"></i>
                  Keys ({pagination?.total ?? apiKeys.length})
                </h5>
                <small className="text-muted">
                  Rotate keys regularly and disable ones no longer in use.
                </small>
              </div>
              {paginationItems}
            </Card.Header>
            <Card.Body className="p-0">
              {isLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading API keys...</span>
                  </Spinner>
                </div>
              ) : apiKeys.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-key fs-1 text-muted mb-3"></i>
                  <h5 className="text-muted">No API keys found</h5>
                  <p className="text-muted">Create your first key to enable external integrations.</p>
                  <Button variant="primary" onClick={openCreateModal}>
                    <i className="fas fa-plus me-2"></i>
                    Create API Key
                  </Button>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Last Used</th>
                        <th>Expires</th>
                        <th>Metadata</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apiKeys.map((apiKey: ApiKey) => (
                        <tr key={apiKey.id}>
                          <td>
                            <div className="fw-semibold">{apiKey.label}</div>
                            <div className="text-muted small">{apiKey.systemName}</div>
                            <div className="text-muted small">Created {formatDateTime(apiKey.createdAt)}</div>
                          </td>
                          <td>
                            <Badge bg={apiKey.isActive ? 'success' : 'secondary'}>
                              {apiKey.isActive ? 'Active' : 'Suspended'}
                            </Badge>
                          </td>
                          <td>
                            <div className="text-muted">
                              {apiKey.lastUsedAt ? (
                                <>
                                  {formatDateTime(apiKey.lastUsedAt)}
                                  <br />
                                  <small>ago: {timeSince(apiKey.lastUsedAt)}</small>
                                </>
                              ) : (
                                <span className="fst-italic">Never</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="text-muted">
                              {apiKey.expiresAt ? (
                                <>
                                  {formatDateTime(apiKey.expiresAt)}
                                  <br />
                                  {new Date(apiKey.expiresAt) < new Date() ? (
                                    <Badge bg="danger" className="mt-1">Expired</Badge>
                                  ) : null}
                                </>
                              ) : (
                                <span className="fst-italic">No expiration</span>
                              )}
                            </div>
                          </td>
                          <td>
                            {apiKey.metadata ? (
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id={`metadata-tooltip-${apiKey.id}`}>
                                    <pre className="mb-0 text-start" style={{ whiteSpace: 'pre-wrap' }}>
                                      {JSON.stringify(apiKey.metadata, null, 2)}
                                    </pre>
                                  </Tooltip>
                                }
                              >
                                <span className="text-primary" role="button">View</span>
                              </OverlayTrigger>
                            ) : (
                              <span className="text-muted">None</span>
                            )}
                          </td>
                          <td>
                            <div className="d-flex justify-content-end gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => openEditModal(apiKey)}
                              >
                                <i className="fas fa-edit"></i>
                              </Button>
                              <Button
                                variant={apiKey.isActive ? 'outline-warning' : 'outline-success'}
                                size="sm"
                                onClick={() =>
                                  toggleStatusMutation.mutate({ id: apiKey.id, isActive: !apiKey.isActive })
                                }
                                disabled={toggleStatusMutation.isPending}
                              >
                                <i className={`fas ${apiKey.isActive ? 'fa-pause-circle' : 'fa-play-circle'}`}></i>
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => {
                                  if (window.confirm('Delete this API key? This action cannot be undone.')) {
                                    deleteMutation.mutate(apiKey.id)
                                  }
                                }}
                                disabled={deleteMutation.isPending}
                              >
                                <i className="fas fa-trash"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
            {apiKeys.length > 0 && paginationItems ? (
              <Card.Footer className="d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  Showing page {pagination?.page ?? page} of {totalPages}
                </small>
                {paginationItems}
              </Card.Footer>
            ) : null}
          </Card>
        </Col>
      </Row>

      <Modal show={showFormModal} onHide={closeFormModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-key me-2"></i>
            {editingKey ? 'Update API Key' : 'Create API Key'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="label">
                  <Form.Label className="fw-semibold">Label *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Integration label"
                    value={formState.label}
                    onChange={(event) => setFormState({ ...formState, label: event.target.value })}
                    required
                  />
                  <Form.Text className="text-muted">
                    Display name for admins (e.g., "Booking App Integration").
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="systemName">
                  <Form.Label className="fw-semibold">System Name *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="System identifier"
                    value={formState.systemName}
                    onChange={(event) => setFormState({ ...formState, systemName: event.target.value })}
                    required
                  />
                  <Form.Text className="text-muted">
                    Appears in audit logs and responses.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="expiresAt">
                  <Form.Label className="fw-semibold">Expiration (optional)</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={formState.expiresAt}
                    onChange={(event) => setFormState({ ...formState, expiresAt: event.target.value })}
                  />
                  <Form.Text className="text-muted">
                    Leave blank for no expiration.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="metadata">
                  <Form.Label className="fw-semibold">Metadata (JSON)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder='{"webhookUrl": "https://example.com"}'
                    value={formState.metadata}
                    onChange={(event) => setFormState({ ...formState, metadata: event.target.value })}
                  />
                  <Form.Text className="text-muted">
                    Optional structured data to store alongside the key.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeFormModal}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting && <Spinner animation="border" size="sm" className="me-2" />}
              {editingKey ? 'Save Changes' : 'Create API Key'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(generatedKey)} onHide={closeGeneratedKeyModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-lock me-2 text-success"></i>
            Store Your API Key
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            Copy this key now. For security reasons it will not be shown again.
          </p>
          <Form.Group>
            <Form.Label className="fw-semibold">{generatedKey?.label}</Form.Label>
            <Form.Control
              type="text"
              readOnly
              value={generatedKey?.key ?? ''}
              onFocus={(event) => event.currentTarget.select()}
            />
          </Form.Group>
          <Form.Text className="text-muted">Rotate this key periodically and treat it like a password.</Form.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={closeGeneratedKeyModal}>
            I have stored it securely
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

function timeSince(value: string | null) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)

  const intervals: Array<[number, string]> = [
    [31536000, 'year'],
    [2592000, 'month'],
    [604800, 'week'],
    [86400, 'day'],
    [3600, 'hour'],
    [60, 'minute']
  ]

  for (const [intervalSeconds, label] of intervals) {
    const count = Math.floor(seconds / intervalSeconds)
    if (count >= 1) {
      return `${count} ${label}${count > 1 ? 's' : ''}`
    }
  }

  return `${seconds} second${seconds !== 1 ? 's' : ''}`
}


